import { useState } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { TextField } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];

interface ParsedAddress {
  street: string;
  aptSuite: string;
  city: string;
  state: string;
  zipCode: string;
}

interface AddressAutocompleteProps {
  label?: string;
  helperText?: string;
  selectedHelperText?: string;
  onAddressSelect: (address: ParsedAddress) => void;
}

interface AddressAutocompleteInputProps extends AddressAutocompleteProps {
  label: string;
  googleMapsApiKey: string;
}

function AddressAutocompleteInput({
  label,
  helperText,
  selectedHelperText,
  onAddressSelect,
  googleMapsApiKey,
}: AddressAutocompleteInputProps) {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [address, setAddress] = useState('');
  const [hasSelectedAddress, setHasSelectedAddress] = useState(false);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const handlePlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    const components = place.address_components || [];
    if (components.length === 0) {
      setHasSelectedAddress(false);
      return;
    }

    const get = (type: string, nameType: 'long_name' | 'short_name' = 'long_name') => {
      const match = components.find((component) => component.types.includes(type));
      return match ? match[nameType] : '';
    };

    const streetNumber = get('street_number');
    const route = get('route');
    const street = [streetNumber, route].filter(Boolean).join(' ');
    const aptSuite = get('subpremise');
    const city = get('locality') || get('sublocality') || get('administrative_area_level_2');
    const state = get('administrative_area_level_1', 'short_name');
    const zipCode = get('postal_code');

    onAddressSelect({ street, aptSuite, city, state, zipCode });
    setAddress(place.formatted_address || '');
    setHasSelectedAddress(true);
  };

  const input = (
    <TextField
      label={label}
      variant="outlined"
      fullWidth
      value={address}
      onChange={(event) => {
        setAddress(event.target.value);
        setHasSelectedAddress(false);
      }}
      helperText={hasSelectedAddress ? selectedHelperText : helperText}
      color={hasSelectedAddress ? 'success' : 'primary'}
      InputProps={{
        endAdornment: hasSelectedAddress ? (
          <CheckCircleOutlineIcon color="success" />
        ) : undefined,
      }}
      FormHelperTextProps={{
        sx: hasSelectedAddress ? { color: 'success.main', fontWeight: 600 } : undefined,
      }}
    />
  );

  if (!googleMapsApiKey || !isLoaded) return input;

  return (
    <Autocomplete onLoad={setAutocomplete} onPlaceChanged={handlePlaceChanged}>
      {input}
    </Autocomplete>
  );
}

export default function AddressAutocomplete({
  label = 'Search Address',
  helperText,
  selectedHelperText,
  onAddressSelect,
}: AddressAutocompleteProps) {
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  if (!googleMapsApiKey) {
    return (
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        helperText={helperText}
      />
    );
  }

  return (
    <AddressAutocompleteInput
      label={label}
      helperText={helperText}
      selectedHelperText={selectedHelperText}
      onAddressSelect={onAddressSelect}
      googleMapsApiKey={googleMapsApiKey}
    />
  );
}
